/**
 * rb-docx.js — real .docx (OOXML) export.
 *
 * Builds a genuine Word document with the vendored `docx` UMD library from the
 * structured ResumeModel — a clean single-column ATS reflow: heading runs,
 * real bullet lists, no layout tables / text boxes / photo / graphics, so an
 * applicant-tracking system parses every word. This is the maximum-ATS
 * artifact (the styled PDF is the pixel-perfect one).
 *
 * Depends on: RbI18n, RbExportText (download + loadScript). Lazy-loads docx.
 * globalThis.RbDocx
 */
(function () {
    'use strict';

    var T = function (k) { return globalThis.RbI18n.t(k); };

    function loadLib() {
        if (globalThis.docx) return Promise.resolve(globalThis.docx);
        return globalThis.RbExportText.loadVendor('docx').then(function () {
            if (!globalThis.docx) throw new Error('docx lib missing');
            return globalThis.docx;
        });
    }

    function build(model, locale, opts) {
        var d = globalThis.docx;
        var D = function (k) { return globalThis.RbI18n.docT(k, model); };
        var fmt = (opts && opts.dateFormat) || 'MM/YYYY';
        var F = function (v) { return globalThis.RbI18n.formatDate(v, fmt); };
        var LV = function (v) { return v === 'native' ? D('prof_native') : String(v || '').toUpperCase(); };
        var rtl = (globalThis.RbI18n.docLang ? globalThis.RbI18n.docLang(model) : (locale || '')).slice(0, 2) === 'ar';
        var FONT = rtl ? 'Arial' : 'Calibri';
        var children = [];

        function run(text, opts) {
            opts = opts || {};
            return new d.TextRun({
                text: String(text == null ? '' : text),
                bold: !!opts.bold,
                size: opts.size || 21,        /* half-points → 10.5pt */
                color: opts.color || '1A1A2E',
                font: FONT,
                rightToLeft: rtl
            });
        }
        function para(runs, opts) {
            opts = opts || {};
            return new d.Paragraph({
                children: Array.isArray(runs) ? runs : [runs],
                spacing: { after: opts.after == null ? 80 : opts.after, before: opts.before || 0 },
                alignment: opts.align || (rtl ? d.AlignmentType.RIGHT : d.AlignmentType.LEFT),
                bidirectional: rtl,
                bullet: opts.bullet ? { level: 0 } : undefined
            });
        }
        function heading(text) {
            return new d.Paragraph({
                children: [run(String(text).toUpperCase(), { bold: true, size: 24, color: '2A2A3A' })],
                spacing: { before: 220, after: 90 },
                border: { bottom: { style: d.BorderStyle.SINGLE, size: 6, color: 'BFBFBF' } },
                bidirectional: rtl,
                alignment: rtl ? d.AlignmentType.RIGHT : d.AlignmentType.LEFT
            });
        }

        var p = model.personal || {};
        /* name + title */
        children.push(new d.Paragraph({
            children: [run(p.name || '', { bold: true, size: 40, color: '111827' })],
            spacing: { after: 40 }, bidirectional: rtl,
            alignment: rtl ? d.AlignmentType.RIGHT : d.AlignmentType.LEFT
        }));
        if (p.title) children.push(para(run(p.title, { size: 24, color: '444444' }), { after: 60 }));

        /* contact line */
        var contact = ['email', 'phone', 'city', 'address', 'website', 'linkedin', 'github']
            .map(function (k) { return p[k]; }).filter(Boolean);
        var details = [];
        var cult = model.cultural || {};
        if (cult.dob && p.dob) details.push(D('date_of_birth') + ': ' + String(p.dob).split('-').reverse().join('.'));
        if (cult.marital && p.marital) details.push(D('marital_' + p.marital));
        if (cult.nationality && p.nationality) details.push(p.nationality);
        if (cult.religion && p.religion) details.push(p.religion);
        if (cult.gender && p.gender) details.push(D('gender_' + p.gender));
        var prefs = globalThis.RbRender && globalThis.RbRender.prefPairs ? globalThis.RbRender.prefPairs(model) : [];
        if (prefs.length) details.push(prefs.map(function (pp) { return pp[0] + ': ' + pp[1]; }).join('   ·   '));
        if (contact.length) children.push(para(run(contact.join('   |   '), { size: 19, color: '555555' }), { after: details.length ? 20 : 120 }));
        if (details.length) children.push(para(run(details.join('  ·  '), { size: 19, color: '555555' }), { after: 120 }));

        function dateStr(a, b, cur) {
            var parts = [F(a), cur ? D('present') : F(b)].filter(Boolean);
            return parts.join(' – ');
        }

        (model.meta.sectionOrder || []).forEach(function (sec) {
            if (sec === 'personal') return;
            if (sec === 'summary') {
                if (model.summary) {
                    children.push(heading(D('section_summary')));
                    String(model.summary).split('\n').filter(function (l) { return l.trim(); }).forEach(function (l) {
                        children.push(para(run(l.trim()), { after: 60 }));
                    });
                }
                return;
            }
            var items = model[sec] || [];
            if (!items.length) return;
            children.push(heading(D('section_' + sec)));

            if (sec === 'experience') {
                items.forEach(function (x) {
                    children.push(para([
                        run(x.position || '', { bold: true, size: 22 }),
                        run(x.company ? '   ' + x.company + (x.location ? ', ' + x.location : '') : '', { size: 21, color: '555555' })
                    ], { after: 10 }));
                    var dr = dateStr(x.startDate, x.endDate, x.current);
                    if (dr) children.push(para(run(dr, { size: 19, color: '777777' }), { after: 30 }));
                    /* one paragraph per line — a single run printed every bullet on one line */
                    String(x.description || '').split('\n').filter(function (a) { return a.trim(); }).forEach(function (a) {
                        children.push(para(run(a.trim()), { bullet: true, after: 20 }));
                    });
                    String(x.achievements || '').split('\n').filter(function (a) { return a.trim(); }).forEach(function (a) {
                        children.push(para(run(a.trim()), { bullet: true, after: 20 }));
                    });
                });
            } else if (sec === 'education') {
                items.forEach(function (x) {
                    children.push(para([
                        run([x.degree, x.field].filter(Boolean).join(', ') || x.institution || '', { bold: true, size: 22 }),
                        run(x.institution && (x.degree || x.field) ? '   ' + x.institution : '', { size: 21, color: '555555' })
                    ], { after: 10 }));
                    var line = [dateStr(x.startDate, x.endDate, false),
                                x.gpa ? D('gpa') + ': ' + x.gpa : '', x.honors].filter(Boolean).join('   ·   ');
                    if (line) children.push(para(run(line, { size: 19, color: '777777' }), { after: 60 }));
                });
            } else if (sec === 'skills') {
                children.push(para(run(items.map(function (s) { return s.name; }).filter(Boolean).join('  ·  ')), { after: 80 }));
            } else if (sec === 'languages') {
                children.push(para(run(items.map(function (l) {
                    return l.name + (l.proficiency ? ' (' + LV(l.proficiency) + ')' : '');
                }).join('  ·  ')), { after: 80 }));
            } else {
                items.forEach(function (x) {
                    var vals = Object.keys(x).filter(function (k) { return k !== 'id' && x[k]; })
                        .map(function (k) { return x[k]; });
                    children.push(para(run(vals.join('  —  ')), { after: 40 }));
                });
            }
        });

        (model.custom || []).forEach(function (cs) {
            if (!cs.title && !(cs.items || []).some(Boolean)) return;
            children.push(heading(cs.title || D('section_custom')));
            (cs.items || []).filter(Boolean).forEach(function (i) {
                children.push(para(run(i), { after: 40 }));
            });
        });

        if (cult.signature) {
            children.push(new d.Paragraph({ text: '', spacing: { before: 360 } }));
            children.push(para(run((p.city || '') + '          ' + (p.name || ''), { size: 19, color: '777777' })));
        }

        /* page size in twips (1/1440") — A4 11906×16838, US Letter 12240×15840 */
        var letter = (model.meta && model.meta.pageSize === 'letter');
        var pageSize = letter ? { width: 12240, height: 15840 } : { width: 11906, height: 16838 };
        return new d.Document({
            creator: 'Timbrica',
            sections: [{
                properties: { page: { size: pageSize, margin: { top: 720, bottom: 720, left: 900, right: 900 } } },
                children: children
            }]
        });
    }

    /* Export — returns a Promise. */
    function exportDocx(model, opts) {
        var locale = globalThis.RbI18n.LOCALE;
        return loadLib().then(function () {
            var doc = build(model, locale, opts);
            return globalThis.docx.Packer.toBlob(doc);
        }).then(function (blob) {
            return globalThis.RbExportText.download(blob, globalThis.RbExportText.safeName(model, 'docx'));
        });
    }

    /* For tests: build + return the blob without downloading. */
    function toBlob(model, locale) {
        return loadLib().then(function () {
            return globalThis.docx.Packer.toBlob(build(model, locale || globalThis.RbI18n.LOCALE));
        });
    }

    globalThis.RbDocx = { exportDocx: exportDocx, toBlob: toBlob, build: build };
})();
